import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { track, identifyUser } from '../../lib/mixpanel';
import { sendLeadToCapi } from '../../lib/metaCapi';
import CircularProgress from '@mui/material/CircularProgress';
import { ReactComponent as Logo } from '../../svg/newlogo.svg';
import { ReactComponent as PhoneSvg } from '../../svg/phonesvg.svg';
import { ReactComponent as SpeechBubble } from '../../svg/speech11bubble.svg';
import styles from '../../styles/sunbaebriefingcentum2pc.module.css';
import {
  useCentumBriefingStats,
  pickRandom,
  formatDday,
  formatRelativeTime,
  SURNAMES,
} from '../../lib/useCentumBriefingStats';
import { BriefingBranchConfig, ALL_BRIEFING_CLOSED } from '../../lib/briefingBranchConfig';

const ChevronDown = () => (
  <svg className={styles.fieldChevron} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M5 7.5L10 12.5L15 7.5" stroke="#333d4b" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

function useCountUp(target: number, duration: number, start: boolean) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!start) return;
    let raf = 0;
    const t0 = performance.now();
    const tick = (now: number) => {
      const p = Math.min(1, (now - t0) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(Math.round(target * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration, start]);
  return val;
}

function SocialProofBand({ config }: { config: BriefingBranchConfig }) {
  const countRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (!countRef.current) return;
    const io = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setVisible(true);
          io.disconnect();
        }
      },
      { threshold: 0.9 }
    );
    io.observe(countRef.current);
    return () => io.disconnect();
  }, []);

  const stats = useCentumBriefingStats(config.location);
  const totalWithOffset = (stats.total ?? 0) + config.totalOffset;
  const applicants = useCountUp(totalWithOffset, 1800, visible && stats.total !== null);

  const tickerLen = stats.recentApplicants.length;
  const [tickIdx, setTickIdx] = useState(0);
  useEffect(() => {
    if (tickerLen === 0) return;
    setTickIdx(i => i % tickerLen);
    const id = setInterval(() => setTickIdx(i => (i + 1) % tickerLen), 2600);
    return () => clearInterval(id);
  }, [tickerLen]);

  const [avatars] = useState(() => pickRandom(SURNAMES, 5));
  void stats.tick;
  const dday = formatDday(config.eventISO);
  const now = new Date();

  return (
    <section className={styles.proof}>
      <div className={styles.proofBg} style={{ backgroundImage: "url('/img/briefing/crowd-hall.jpeg')" }} />
      <div className={styles.proofOverlay} />
      <div className={styles.proofScrim} />
      <div className={styles.proofVignette} />

      <div className={styles.proofInner}>
        <div className={styles.proofTop}>
          <div className={styles.proofEyebrow}>
            <span className={styles.proofPulse} />
            LIVE · 실시간 신청 현황
          </div>
          <div className={styles.proofDday}>
            <b>{dday}</b> 마감
          </div>
        </div>

        <div className={styles.proofMain}>
          <p className={styles.proofCountLabel}>지금까지 설명회를 신청한 학생·학부모</p>
          <div className={styles.proofCount} ref={countRef}>
            <span className={styles.proofNum}>
              {stats.total !== null && applicants > 0 ? applicants.toLocaleString() : '—'}
            </span>
            <span className={styles.proofUnit}>명</span>
          </div>
          <p className={styles.proofCountSub}>
            최근 24시간{' '}
            <span className={styles.pos}>
              {stats.last24h !== null ? `+${stats.last24h + config.last24hOffset}명` : '—'}
            </span>{' '}
            신청
          </p>
        </div>

        <div className={styles.proofAvatars}>
          <div className={styles.proofAvatarsStack}>
            {avatars.map((n, i) => (
              <div className={styles.proofAvatar} key={i}>{n}</div>
            ))}
          </div>
          <span className={styles.proofAvatarsCaption}>
            지금 이 페이지를 함께 보고 있어요
          </span>
        </div>

        <div className={styles.proofTicker}>
          <span className={styles.proofTickerDot} />
          <div className={styles.proofTickerList}>
            {stats.recentApplicants.map((a, i) => (
              <div
                key={`${a.createdAt}-${i}`}
                className={`${styles.proofTickerItem} ${i === tickIdx ? styles.isActive : ''}`}
              >
                <span><b>{a.name}</b>님 설명회 신청</span>
                <span className={styles.proofTickerTime}>{formatRelativeTime(a.createdAt, now)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export default function SunbaeBriefingBranchPc({ config }: { config: BriefingBranchConfig }) {
  const navigate = useNavigate();

  const [attendanceType, setAttendanceType] = useState('');
  const [gender, setGender] = useState('');
  const [name, setName] = useState('');
  const [phoneMiddle, setPhoneMiddle] = useState('');
  const [phoneLast, setPhoneLast] = useState('');
  const [enrollmentType, setEnrollmentType] = useState('');
  const [loading, setLoading] = useState(false);
  const [showFixedBar, setShowFixedBar] = useState(true);
  const phoneLastRef = useRef<HTMLInputElement>(null);
  const infoCardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      if (infoCardRef.current) {
        const rect = infoCardRef.current.getBoundingClientRect();
        setShowFixedBar(rect.bottom > 0);
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isFormValid =
    attendanceType !== '' &&
    gender !== '' &&
    name.trim() !== '' &&
    phoneMiddle.length === 4 &&
    phoneLast.length === 4 &&
    enrollmentType !== '';

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const fbc = params.get('fbc');
    const fbp = params.get('fbp');
    if (fbc && fbc.length > 0) {
      document.cookie = `_fbc=${fbc}; domain=.suneungsunbae.com; path=/; max-age=7776000; secure; samesite=lax`;
    }
    if (fbp && fbp.length > 0) {
      document.cookie = `_fbp=${fbp}; domain=.suneungsunbae.com; path=/; max-age=7776000; secure; samesite=lax`;
    }
  }, []);

  useEffect(() => {
    track('Waiting List Page Viewed', { page_type: config.mixpanelPageType });
  }, [config.mixpanelPageType]);

  const submit = () => {
    if (ALL_BRIEFING_CLOSED) return;

    const telephoneNumber = '010' + phoneMiddle + phoneLast;

    setLoading(true);

    const data = {
      location: config.location,
      select: gender,
      select2: enrollmentType,
      name,
      telephoneNumber,
      cert: '111111',
      etc: attendanceType,
      year: 2026,
      type1: 'winter26',
    };

    fetch('https://peetsunbae.com/waiting/submit', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ data }),
    })
      .then((res: any) => res.json())
      .then((result: any) => {
        setLoading(false);

        if (result.message === 'fail' || result.message === 'error') {
          track('Waiting List Submit Failed', {
            reason: result.message,
            location: config.location,
            form_type: config.formType,
          });
          alert('오류 관리자 문의(010-9880-0489)');
          return;
        }

        if (result.message === 'success') {
          identifyUser(telephoneNumber, {
            $name: name,
            $phone: telephoneNumber,
            waiting_list_location: config.location,
            student_type: enrollmentType,
            gender,
            attendance_type: attendanceType,
            waiting_list_date: new Date().toISOString(),
          });
          track('Waiting List Submitted', {
            location: config.location,
            gender,
            attendance_type: attendanceType,
            student_type: enrollmentType,
            form_type: config.formType,
          });
          const eventId = `lead_briefing_${telephoneNumber}_${Date.now()}`;
          if ((window as any).fbq) {
            (window as any).fbq(
              'track',
              'Lead',
              {
                content_name: 'briefing_request',
                source: 'briefing',
                value: 100000,
                currency: 'KRW',
              },
              { eventID: eventId }
            );
          }
          sendLeadToCapi({
            eventId,
            phone: telephoneNumber,
            name,
            location: config.location,
            contentName: 'briefing_request',
          });
          navigate('/registerwaitingfinish', { state: { id: result.id } });
        }
      });
  };

  return (
    <>
      <div className={styles.page}>
        <header className={styles.landingHeader}>
          <a className={styles.landingLogo} href="#" aria-label="수능선배">
            <Logo className={styles.landingLogoImg} />
          </a>
        </header>

        <div className={styles.eventHeader}>
          <h2 className={styles.eventEyebrow}>
            {config.heroEyebrowPc.split('\n').map((line, i, arr) => (
              <React.Fragment key={i}>
                {line}
                {i < arr.length - 1 ? <br /> : null}
              </React.Fragment>
            ))}
          </h2>
          <p className={styles.eventTagline}>{config.heroTagline}</p>
          <hr className={styles.hair} />
        </div>

        <div className={styles.eventMeta}>
          <div className={styles.metaCol}>
            <span className={styles.metaKey}>일시</span>
            <span className={styles.metaVal}>{config.eventDateMeta}</span>
            <span className={styles.metaSub}>신청자 문자 공지</span>
          </div>
          <div className={styles.metaCol}>
            <span className={styles.metaKey}>장소</span>
            <span className={styles.metaVal}>{config.metaPlaceLine1Pc}</span>
            <span className={styles.metaSub}>{config.metaPlaceLine2Pc}</span>
          </div>
          <div className={styles.metaCol}>
            <span className={styles.metaKey}>대상</span>
            <span className={styles.metaVal}>재학생 · N수생</span>
            <span className={styles.metaSub}>학부모 동반 가능</span>
          </div>
          <div className={styles.metaCol}>
            <span className={styles.metaKey}>정원</span>
            <span className={styles.metaVal}>선착순 진행</span>
            <span className={styles.metaSub}>마감 시 대기 등록</span>
          </div>
        </div>

        <SocialProofBand config={config} />

        <section className={styles.cntSection}>
          <div className={styles.cntHeading}>
            <h2 className={styles.cntHeadingTitle}>
              {'참여자 전원에게 드리는\n한정 혜택'}
            </h2>
          </div>

          <div>
            <div className={styles.benefitRow}>
              <div className={styles.benefitText}>
                {'Medical 296 Databook\n8개 유형으로 분류한\n합격 멘토 296인의 점수 · 진도 · 노하우'}
              </div>
              <div className={styles.benefitImage}>
                <video
                  className={styles.benefitVideo}
                  src="https://pub-81fe206b05a84c2da16285ce68449232.r2.dev/sunbae/blog/1/f_c_f_mp_.mp4"
                  autoPlay
                  muted
                  loop
                  playsInline
                  preload="auto"
                />
                <div className={styles.sticker}>
                  <div className={styles.stickerEn}>SUNBAE</div>
                  <div className={styles.stickerSub}>독점 제공</div>
                </div>
              </div>
            </div>
            <div className={styles.benefitRow}>
              <div className={styles.benefitText}>{config.benefitRegistrationText}</div>
              <div className={styles.benefitImage}>
                <img
                  className={styles.benefitImg}
                  src="https://pub-81fe206b05a84c2da16285ce68449232.r2.dev/sunbae/blog/1/pc2.webp"
                  alt=""
                />
              </div>
            </div>
            <div className={styles.benefitRow}>
              <div className={styles.benefitText}>좌석 우선 지정권</div>
              <div className={styles.benefitImage}>
                <img
                  className={styles.benefitImg}
                  src="https://pub-81fe206b05a84c2da16285ce68449232.r2.dev/sunbae/blog/1/pc3.webp"
                  alt=""
                />
              </div>
            </div>
          </div>
        </section>

        <section className={styles.cntSection}>
          <div className={styles.cntHeading}>
            <h2 className={styles.cntHeadingTitle}>설명회 예약하기</h2>
          </div>

          <div className={styles.formWrap}>
            <div className={styles.infoCard} ref={infoCardRef}>
              <p className={styles.infoCardTitle}>{config.pageTitle}</p>
              <hr className={styles.hair} />
              <div className={styles.infoList}>
                <div className={styles.infoRow}>
                  <ul style={{ margin: 0, paddingLeft: 30 }}>
                    <li><span>일시</span></li>
                  </ul>
                  <p className={styles.infoValue}>{config.eventDateFull}</p>
                </div>
                <div className={styles.infoRow}>
                  <ul style={{ margin: 0, paddingLeft: 30 }}>
                    <li><span>대상</span></li>
                  </ul>
                  <p className={styles.infoValue}>재학생 / N수생 (학부모 동반 가능)</p>
                </div>
                <div className={styles.infoRow}>
                  <ul style={{ margin: 0, paddingLeft: 30 }}>
                    <li><span>장소</span></li>
                  </ul>
                  <p className={styles.infoValue}>{config.infoCardAddressPc}</p>
                </div>
              </div>
              <hr className={styles.hair} />
              <p className={styles.infoDisclaimer}>
                예약은 선착순으로 진행되며, 예약 상황에 따라 조기 마감될 수 있습니다.<br />
                등록된 연락처로 설명회 안내 문자를 발송해 드리오니, 정확하게 입력해 주시기 바랍니다.
              </p>
            </div>

            <div className={styles.form}>
              <div className={styles.formFields}>
                <div className={styles.formRow}>
                  <p className={styles.formLabel}>참석 구분</p>
                  <div className={styles.fieldWrapper}>
                    <select
                      className={!attendanceType ? styles.placeholder : ''}
                      value={attendanceType}
                      disabled={ALL_BRIEFING_CLOSED}
                      onChange={e => setAttendanceType(e.target.value)}
                    >
                      <option value="" disabled>참석 구분을 선택해주세요</option>
                      <option value="attend">설명회 참석</option>
                      <option value="sms">안내 문자 수신</option>
                    </select>
                    <ChevronDown />
                  </div>
                </div>

                <div className={styles.formRow}>
                  <p className={styles.formLabel}>학생 성별</p>
                  <div className={styles.fieldWrapper}>
                    <select
                      className={!gender ? styles.placeholder : ''}
                      value={gender}
                      disabled={ALL_BRIEFING_CLOSED}
                      onChange={e => setGender(e.target.value)}
                    >
                      <option value="" disabled>학생 성별을 선택해주세요</option>
                      <option value="male">남학생</option>
                      <option value="female">여학생</option>
                    </select>
                    <ChevronDown />
                  </div>
                </div>

                <div className={styles.formRow}>
                  <p className={styles.formLabel}>성명</p>
                  <input
                    className={styles.textInput}
                    type="text"
                    placeholder="성명을 입력해 주세요"
                    value={name}
                    disabled={ALL_BRIEFING_CLOSED}
                    onChange={e => setName(e.target.value)}
                  />
                </div>

                <div className={styles.formRow}>
                  <p className={styles.formLabel}>연락처</p>
                  <div className={styles.fieldWrapper}>
                    <select value="010" onChange={() => {}}>
                      <option value="010">010</option>
                    </select>
                    <ChevronDown />
                  </div>
                  <input
                    className={styles.phoneInput}
                    type="text"
                    inputMode="numeric"
                    maxLength={4}
                    value={phoneMiddle}
                    disabled={ALL_BRIEFING_CLOSED}
                    onChange={e => {
                      const val = e.target.value.replace(/\D/g, '');
                      setPhoneMiddle(val);
                      if (val.length === 4) phoneLastRef.current?.focus();
                    }}
                  />
                  <input
                    ref={phoneLastRef}
                    className={styles.phoneInput}
                    type="text"
                    inputMode="numeric"
                    maxLength={4}
                    value={phoneLast}
                    disabled={ALL_BRIEFING_CLOSED}
                    onChange={e => setPhoneLast(e.target.value.replace(/\D/g, ''))}
                  />
                </div>

                <div className={styles.formRow}>
                  <p className={styles.formLabel}>재학 구분</p>
                  <div className={styles.fieldWrapper}>
                    <select
                      className={!enrollmentType ? styles.placeholder : ''}
                      value={enrollmentType}
                      disabled={ALL_BRIEFING_CLOSED}
                      onChange={e => setEnrollmentType(e.target.value)}
                    >
                      <option value="" disabled>재학 구분을 선택해주세요</option>
                      <option value="current">재학생</option>
                      <option value="n">N수생</option>
                    </select>
                    <ChevronDown />
                  </div>
                </div>
              </div>

              <button
                className={styles.submitBtn}
                type="button"
                disabled={ALL_BRIEFING_CLOSED || !isFormValid || loading}
                onClick={submit}
              >
                {ALL_BRIEFING_CLOSED
                  ? <span className={styles.submitBtnText}>예약 마감</span>
                  : loading
                    ? <CircularProgress color="inherit" size={24} />
                    : (
                      <>
                        <span className={styles.submitBtnText}>예약하기</span>
                        <svg className={styles.submitBtnArrow} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                          <path d="M5 12h14M13 6l6 6-6 6" />
                        </svg>
                      </>
                    )
                }
              </button>
            </div>
          </div>
        </section>
      </div>

      <div className={styles.fixedDiv} style={{ display: showFixedBar ? 'block' : 'none' }}>
        <div className={styles.fixedDivInner}>
          <div className={styles.fixedDivInner1}>
            <Logo className={styles.fixedDivLogoImg} />
            <div className={styles.fixedDivVerticalLine} />
            <div className={styles.fixedDivText}>
              {config.fixedBarText.split('\n').map((line, i, arr) => (
                <React.Fragment key={i}>
                  {line}
                  {i < arr.length - 1 ? <br /> : null}
                </React.Fragment>
              ))}
            </div>
          </div>
          <div className={styles.fixedDivInner2}>
            <button
              className={styles.fixedDivBtn1}
              onClick={() => window.open(
                `/introducedetail?location=${encodeURIComponent(config.introduceLocationKey)}`,
                '_blank'
              )}
            >
              {config.fixedBarIntroLabel}
            </button>
            <button
              className={styles.fixedDivBtn2}
              onClick={() => { window.location.href = config.telHref; }}
            >
              <PhoneSvg className={styles.fixedDivBtn2Img} />
            </button>
            <button
              className={styles.fixedDivBtn2}
              onClick={() => window.open(config.kakaoUrl, '_blank')}
            >
              <SpeechBubble className={styles.fixedDivBtn2Img} />
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
